<template>
  <div>
    <section
      v-if="$auth.isAuthenticated"
      class="w-full"
    >
      <h1 class="mb-6 text-2xl font-bold">
        {{ $t('components.company.connections.title') }}
      </h1>

      <div class="flex flex-wrap gap-4">
        <ConnectionOverviewCard
          :title="$t('components.company.connections.crm')"
          :rows="crmRows"
        />

        <ConnectionOverviewCard
          :title="$t('components.company.connections.socialMedia')"
          :rows="socialRows"
        />

        <ConnectionOverviewCard
          v-if="isFeatureActive(featureNames.GOOGLE_CALENDAR)"
          :title="$t('components.company.connections.googleCalendar')"
          :rows="googleCalendarRows"
        />

        <ConnectionOverviewCard
          v-if="isFeatureActive(featureNames.KONSTANTINE)"
          :title="$t('components.company.connections.aiAgents')"
          :rows="konstantineRows"
        />
      </div>
      <Feature :feature-slug="featureNames.EXPORT_CONNECTIONS_CSV">
        <div class="flex items-end mt-5">
          <div class="flex flex-col mr-5">
            <label
              for="export-platform"
              class="mb-1 text-sm font-semibold text-gray-600"
            >
              {{ $t('components.company.connections.platform') }}
            </label>

            <select
              id="export-platform"
              v-model="exportPlatform"
              class="px-3 py-2 border border-gray-300 rounded"
            >
              <option
                v-for="option in exportPlatformOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
          </div>
          <div
            class="flex items-center px-3 py-2 font-semibold text-gray-600 uppercase cursor-pointer hover:text-gray-800"
            :class="{
              'cursor-pointer hover:text-gray-800': !exportingConnections,
              'cursor-not-allowed opacity-50': exportingConnections
            }"
            @click="exportConnectionsStatus(exportPlatform)"
          >
            <span v-if="exportingConnections">
              {{ $t('components.company.connections.exporting') }}
            </span>

            <span v-else>
              {{ $t('components.company.connections.export-button') }}
            </span>
          </div>
        </div>
      </Feature>
    </section>
  </div>
</template>

<script>
import GET_CRM_COMPANY_CONFIG from '@/graphql/GetCrmCompanyConfig.gql'
import GET_SOCIAL_MEDIA_CONNECTIONS from '@/graphql/GetSocialMediaConnections.gql'
import GET_FACEBOOK_PAGES from '@/graphql/GetFacebookPages.gql'
import GET_LINKEDIN_PAGES from '@/graphql/GetLinkedinOrganizations.gql'
import GET_CALENDAR_SYNC_SETTINGS from '@/graphql/GetCalendarSyncSettings.gql'
import GET_KONSTANTINE_CONNECTION from '@/graphql/GetKonstantineConnection.gql'
import EXPORT_CONNECTIONS_STATUS_CSV from '@/graphql/ExportCustomerConnections.gql'
import ConnectionOverviewCard from './connections-overview-card.vue'
import { triggerExport } from '@/lib/export'
import featureMixin from '@/mixins/feature'
import { Feature } from 'feature-config'

export default {
  components: {
    Feature,
    ConnectionOverviewCard
  },

  mixins: [featureMixin],

  props: {
    companyId: {
      type: String,
      required: true
    },
    platform: {
      type: String,
      required: false
    }
  },

  data () {
    const platform = this.platform?.toLowerCase()
    return {
      crmConfig: null,
      calendarSyncSettingsPerUser: null,
      konstantineConnection: null,
      socialMediaConnections: [],
      metaPages: [],
      linkedinPages: null,
      exportingConnections: false,
      exportPlatform: platform,
      exportPlatformOptions: [
        {
          text: 'SMG',
          value: 'smg'
        },
        {
          text: 'FALC',
          value: 'falc'
        },
        {
          text: 'BOTTIMMO',
          value: 'bottimmo'
        }
      ].filter(option => option.value === platform)
    }
  },

  apollo: {
    crmConfig: {
      query: GET_CRM_COMPANY_CONFIG,
      variables () {
        return {
          companyId: this.companyId || this.$auth?.user?.companyId
        }
      },
      update (data) {
        return data.config
      }
    },

    calendarSyncSettingsPerUser: {
      query: GET_CALENDAR_SYNC_SETTINGS,
      variables () {
        return {
          companyId: this.companyId || this.$auth?.user?.companyId
        }
      },
      update (data) {
        return data.calendarSyncSettingsPerUser || []
      }
    },

    konstantineConnection: {
      query: GET_KONSTANTINE_CONNECTION,
      variables () {
        return {
          companyId: this.companyId || this.$auth?.user?.companyId
        }
      },
      update (data) {
        return data.konstantine?.getConnection || null
      },
      error () {
        this.konstantineConnection = null
      }
    },

    socialMediaConnections: {
      query: GET_SOCIAL_MEDIA_CONNECTIONS,
      variables () {
        return {
          companyId: this.companyId || this.$auth?.user?.companyId
        }
      },
      update (data) {
        return data.socialMediaConnections || []
      }
    },

    metaPages: {
      query: GET_FACEBOOK_PAGES,
      variables () {
        return {
          companyId: this.companyId || this.$auth?.user?.companyId
        }
      },
      update (data) {
        return data.metaPages || []
      }
    },

    linkedinPages: {
      query: GET_LINKEDIN_PAGES,
      variables () {
        return {
          companyId: this.companyId || this.$auth?.user?.companyId
        }
      },
      update (data) {
        return data.linkedinPages || []
      },
      error () {
        this.linkedinPages = null
      }
    }
  },

  computed: {
    isSmgPlatform () {
      return this.platform?.toLowerCase() === 'smg'
    },
    isBottimmoPlatform () {
      return this.platform?.toLowerCase() === 'bottimmo'
    },
    isFalcPlatform () {
      return this.platform?.toLowerCase() === 'falc'
    },
    crmRows () {
      const allProviders = [
        'FLOWFACT',
        'PROPSTACK',
        'ONOFFICE',
        'CASAONE',
        'EMPRO'
      ]

      const activeProvider = this.crmConfig?.crmProvider
      const isActive = this.crmConfig?.isActive

      return allProviders.map(provider => {
        const connected = provider === activeProvider && isActive

        return {
          label: this.formatCrmProvider(provider),
          value: connected ? this.$t('components.company.connections.connected') : this.$t('components.company.connections.notConnected'),
          connected
        }
      })
    },

    socialRows () {
      const connectedTypes = this.socialMediaConnections.map(type =>
        String(type).toUpperCase()
      )

      const facebookPageNames = this.metaPages
        .filter(page => page.isActive)
        .map(page => page.name)
        .join(', ')
      const instagramPageNames = this.metaPages
        .filter(page => !!page.instagram_business_account && page.isActive)
        .map(page => page.instagram_business_account.username)
        .join(', ')

      const linkedinPageNames = (this.linkedinPages || [])
        .map(page => page.name)
        .join(', ')

      const facebookConnected = connectedTypes.includes('FACEBOOK')
      const instagramConnected = this.metaPages.some(
        page => !!page.instagram_business_account && page.isActive
      )
      const linkedinConnected = connectedTypes.includes('LINKEDIN') &&
        Array.isArray(this.linkedinPages) && this.linkedinPages?.length > 0

      return [
        {
          label: 'Facebook',
          value: facebookConnected
            ? `${this.$t('components.company.connections.connected')}${facebookPageNames ? ` (${facebookPageNames})` : ''}`
            : this.$t('components.company.connections.notConnected'),
          connected: facebookConnected
        },
        {
          label: 'Instagram',
          value: instagramConnected
            ? `${this.$t('components.company.connections.connected')}${instagramPageNames ? ` (${instagramPageNames})` : ''}`
            : this.$t('components.company.connections.notConnected'),
          connected: instagramConnected
        },
        {
          label: 'LinkedIn',
          value: linkedinConnected
            ? `${this.$t('components.company.connections.connected')}${linkedinPageNames ? ` (${linkedinPageNames})` : ''}`
            : this.$t('components.company.connections.notConnected'),
          connected: linkedinConnected
        }
      ]
    },

    googleCalendarRows () {
      if (!this.calendarSyncSettingsPerUser?.length) {
        return [
          {
            label: this.$t('components.company.connections.googleCalendar'),
            value: this.$t('components.company.connections.notConnected'),
            connected: false
          },
          {
            label: this.$t('components.company.connections.calendarSync'),
            value: this.$t('components.company.connections.inactive'),
            connected: false
          }
        ]
      }

      const sortedUsers = [...this.calendarSyncSettingsPerUser].sort((a, b) => {
        const aActive = !!a.settings?.syncGoogleCalendar
        const bActive = !!b.settings?.syncGoogleCalendar

        return Number(bActive) - Number(aActive)
      })

      return sortedUsers.flatMap(user => {
        const connected = !!user.settings?.googleCalendarConnected

        return [
          {
            userName: user.userName,
            label: this.$t('components.company.connections.googleCalendar'),
            value: connected ? this.$t('components.company.connections.connected') : this.$t('components.company.connections.notConnected'),
            connected
          },
          {
            label: this.$t('components.company.connections.calendarSync'),
            value: user.settings?.syncGoogleCalendar ? this.$t('components.company.connections.active') : this.$t('components.company.connections.inactive'),
            connected: !!user.settings?.syncGoogleCalendar
          }
        ]
      })
    },

    konstantineRows () {
      const connected = this.konstantineConnection?.status === 'ACTIVE'

      return [
        {
          label: 'Konstantine',
          value: connected ? this.$t('components.company.connections.connected') : this.$t('components.company.connections.notConnected'),
          connected
        }
      ]
    }
  },

  methods: {
    async exportConnectionsStatus (platform) {
      if (this.exportingConnections) return

      this.exportingConnections = true

      try {
        const apolloClient = this.$apollo.provider.defaultClient

        const { data } = await apolloClient.query({
          query: EXPORT_CONNECTIONS_STATUS_CSV,
          variables: {
            platform
          },
          fetchPolicy: 'network-only'
        })

        triggerExport({
          filename: `Verbindungen-${platform.toUpperCase()}.csv`,
          data: data.generateCustomerConnectionCsv,
          type: 'text/csv;charset=utf-8'
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        this.exportingConnections = false
      }
    },
    formatCrmProvider (provider) {
      const labels = {
        FLOWFACT: 'Flowfact',
        PROPSTACK: 'Propstack',
        ONOFFICE: 'onOffice',
        CASAONE: 'Casaone',
        EMPRO: 'Empro'
      }

      return labels[provider] || provider || 'CRM'
    }
  }
}
</script>
